import { useEffect, useState } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import ThemeToggle from "../components/ThemeToggle";
import { Shield, Clock, Euro, TrendingUp, Users, Building2, RefreshCw, LogOut, Activity, Circle, FileDown, FileText } from "lucide-react";

const fmtHours = (h) => {
  if (h == null) return "0 h";
  if (h < 1) return `${Math.round(h * 60)} min`;
  return `${h.toFixed(h < 10 ? 1 : 0)} h`;
};

const fmtEur = (v) => new Intl.NumberFormat("sl-SI", { style: "currency", currency: "EUR", maximumFractionDigits: 2 }).format(v || 0);

const fmtDateTime = (iso) => iso ? new Date(iso).toLocaleString("sl-SI", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "";

// Prijaznejši prevod audit action verbov v slovenske labele
const ACTION_LABELS = {
  "user.create": "Dodal člana",
  "user.update": "Uredil člana",
  "user.credentials_change": "Spremenil poverilnice",
  "user.delete": "Izbrisal člana",
  "users.import_csv": "CSV uvoz članov",
  "debt.create": "Ustvaril dolg",
  "debt.update": "Uredil dolg",
  "debt.delete": "Izbrisal dolg",
  "debt.pay": "Označil plačilo",
  "event.create": "Dodal dogodek",
  "event.update": "Uredil dogodek",
  "event.delete": "Izbrisal dogodek",
  "hours.approve": "Potrdil ure",
  "hours.reject": "Zavrnil ure",
};
const actionLabel = (a) => ACTION_LABELS[a] || a;

const PlanLabel = ({ p }) => {
  const map = {
    monthly: "Mesečna",
    yearly: "Letna",
    trial: "Preizkus",
  };
  return map[p] || p;
};

const SuperAdminProfile = () => {
  const { user, token, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/superadmin/dashboard-stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStats(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Napaka pri branju statistike");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const exportSessions = async (format) => {
    try {
      const res = await axios.get(`${API}/superadmin/sessions/export?format=${format}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: format === "pdf" ? "application/pdf" : "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 16).replace(/[T:]/g, "-");
      a.href = url;
      a.download = `sa-seje-${stamp}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Izvoz ${format.toUpperCase()} prenesen`);
    } catch (e) {
      toast.error("Napaka pri izvozu");
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="super-admin-profile">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-14 h-14 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center">
            <Shield className="h-7 w-7 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-primary">
              {user?.ime} {user?.priimek}
            </h1>
            <div className="flex items-center gap-2 mt-0.5">
              <Badge className="bg-primary text-primary-foreground">Super-admin</Badge>
              <span className="text-xs text-muted-foreground">{user?.lovska_izkaznica}</span>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="sa-profile-refresh">
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} />
            Osveži
          </Button>
          <ThemeToggle />
          <Button variant="ghost" size="sm" onClick={logout} data-testid="sa-profile-logout">
            <LogOut className="h-4 w-4 mr-1" />Odjava
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="spinner" /></div>
      ) : !stats ? (
        <Card><CardContent className="p-6 text-center text-sm text-muted-foreground">Statistika ni na voljo.</CardContent></Card>
      ) : (
        <>
          {/* Top 4 stat cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Card data-testid="sa-stat-hours-total">
              <CardContent className="p-4 space-y-1">
                <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-wide">
                  <Clock className="h-3.5 w-3.5" /> SA ure skupaj
                </div>
                <p className="text-2xl font-bold text-primary">{fmtHours(stats.sa_hours_total)}</p>
                <p className="text-[10px] text-muted-foreground">{stats.sessions_count} sej upravljanja LD</p>
              </CardContent>
            </Card>
            <Card data-testid="sa-stat-hours-month">
              <CardContent className="p-4 space-y-1">
                <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-wide">
                  <TrendingUp className="h-3.5 w-3.5" /> Ta mesec
                </div>
                <p className="text-2xl font-bold text-primary">{fmtHours(stats.sa_hours_this_month)}</p>
                <p className="text-[10px] text-muted-foreground">upravljanje LD</p>
              </CardContent>
            </Card>
            <Card data-testid="sa-stat-revenue-total">
              <CardContent className="p-4 space-y-1">
                <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-wide">
                  <Euro className="h-3.5 w-3.5" /> Prihodek skupaj
                </div>
                <p className="text-2xl font-bold text-green-700 dark:text-green-400">{fmtEur(stats.revenue_total_eur)}</p>
                <p className="text-[10px] text-muted-foreground">{stats.active_subscriptions_count} aktivnih naročnin</p>
              </CardContent>
            </Card>
            <Card data-testid="sa-stat-revenue-month">
              <CardContent className="p-4 space-y-1">
                <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-wide">
                  <Euro className="h-3.5 w-3.5" /> Prihodek ta mesec
                </div>
                <p className="text-2xl font-bold text-green-700 dark:text-green-400">{fmtEur(stats.revenue_this_month_eur)}</p>
                <p className="text-[10px] text-muted-foreground">samo plačane transakcije</p>
              </CardContent>
            </Card>
          </div>

          {/* Revenue by plan */}
          <Card data-testid="sa-revenue-by-plan">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Euro className="h-4 w-4" /> Prihodek po naročninah
              </CardTitle>
              <CardDescription className="text-xs">Razdelitev po planu (mesečna vs. letna).</CardDescription>
            </CardHeader>
            <CardContent>
              {(!stats.revenue_by_plan || stats.revenue_by_plan.length === 0) ? (
                <p className="text-sm text-muted-foreground text-center py-4">Še ni plačanih naročnin.</p>
              ) : (
                <div className="space-y-2">
                  {stats.revenue_by_plan.map(p => (
                    <div key={p.plan} className="flex items-center justify-between p-2 border rounded" data-testid={`sa-plan-${p.plan}`}>
                      <div>
                        <p className="font-medium text-sm"><PlanLabel p={p.plan} /></p>
                        <p className="text-[11px] text-muted-foreground">{p.count} plačil</p>
                      </div>
                      <p className="text-lg font-semibold text-green-700 dark:text-green-400">{fmtEur(p.total_eur)}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent 5 sessions timeline */}
          <Card data-testid="sa-recent-sessions">
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Activity className="h-4 w-4" /> Zadnjih 5 sej upravljanja LD
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Kaj ste izvedli med posamezno impersonacijo. Odprte seje označene z zelenim pulzom.
                  </CardDescription>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <Button size="sm" variant="outline" onClick={() => exportSessions("csv")} data-testid="sa-export-csv" title="Izvoz vseh sej v CSV (za Excel)">
                    <FileDown className="h-3.5 w-3.5 mr-1" />CSV
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => exportSessions("pdf")} data-testid="sa-export-pdf" title="Izvoz vseh sej v PDF (za dokazilo)">
                    <FileText className="h-3.5 w-3.5 mr-1" />PDF
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {(!stats.recent_sessions || stats.recent_sessions.length === 0) ? (
                <p className="text-sm text-muted-foreground text-center py-4">Še ni bilo sej.</p>
              ) : (
                <ol className="space-y-3">
                  {stats.recent_sessions.map((s, idx) => (
                    <li key={idx} className="relative pl-6 pb-3 border-l-2 border-primary/20 last:border-transparent" data-testid={`sa-recent-session-${idx}`}>
                      <span className={`absolute -left-[7px] top-0.5 h-3 w-3 rounded-full ${s.is_open ? "bg-green-500 animate-ping-slow" : "bg-primary"}`} />
                      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                        <p className="font-medium text-sm">{s.ld_naziv}</p>
                        {s.is_open && (
                          <Badge variant="outline" className="text-[10px] text-green-700 border-green-500">
                            <Circle className="h-2 w-2 mr-0.5 fill-green-500 text-green-500" />
                            odprta
                          </Badge>
                        )}
                        <span className="text-[11px] text-muted-foreground">
                          {fmtDateTime(s.start_at)}
                          {s.end_at ? ` → ${new Date(s.end_at).toLocaleTimeString("sl-SI", { hour: "2-digit", minute: "2-digit" })}` : " → …"}
                        </span>
                        <span className="text-[11px] font-medium text-primary ml-auto">
                          {s.duration_minutes < 1 ? "< 1 min" : `${s.duration_minutes < 60 ? Math.round(s.duration_minutes) + " min" : (s.duration_minutes / 60).toFixed(1) + " h"}`}
                        </span>
                      </div>
                      {s.actions_count > 0 ? (
                        <div className="mt-1 flex flex-wrap items-center gap-1">
                          <span className="text-[11px] text-muted-foreground">Akcije ({s.actions_count}):</span>
                          {s.actions_breakdown.map(a => (
                            <Badge key={a.action} variant="secondary" className="text-[10px]">
                              {actionLabel(a.action)} × {a.count}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <p className="text-[11px] text-muted-foreground italic mt-0.5">Brez zabeleženih akcij (samo pregled)</p>
                      )}
                    </li>
                  ))}
                </ol>
              )}
            </CardContent>
          </Card>

          {/* Sessions per LD */}
          <Card data-testid="sa-sessions-by-ld">            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Building2 className="h-4 w-4" /> Upravljanje po LD (top 10)
              </CardTitle>
              <CardDescription className="text-xs">
                Skupno število vstopov v posamezno LD in približne porabljene ure.
                Trenutno odprte seje nad 6h so avtomatsko zapreta v izračunu.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {(!stats.sessions_by_ld || stats.sessions_by_ld.length === 0) ? (
                <p className="text-sm text-muted-foreground text-center py-4">Še niste vstopili v nobeno LD kot predsednik.</p>
              ) : (
                <div className="space-y-1.5">
                  {stats.sessions_by_ld.map((s, idx) => (
                    <div key={s.ld_id || idx} className="flex items-center gap-3 p-2 border rounded" data-testid={`sa-ld-session-${idx}`}>
                      <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-[11px] font-medium text-primary">
                        {idx + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{s.ld_naziv}</p>
                        <p className="text-[11px] text-muted-foreground">{s.sessions} sej</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold">{fmtHours(s.hours)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <p className="text-[10px] text-muted-foreground text-center">
            Statistika osvežena: {new Date(stats.generated_at).toLocaleString("sl-SI")}
          </p>
        </>
      )}
    </div>
  );
};

export default SuperAdminProfile;
